/**
 * Dialogue Lines Service
 * Downloads and caches episode audio files for the current language.
 * Audio files are fetched from the GCS bucket (or local dev server) and cached
 * as Blob URLs to avoid re-downloading during a session.
 */

import { getDialogueLinesBaseUrl } from './dialogue-lines-config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DialogueLinesManifest {
  sessionScriptFolder: string;
  languages: string[];
  dialogues: Record<string, DialogueLinesEntry>;
}

interface DialogueLinesEntry {
  folder: string;
  lineCount: number;
}

// lineKey: `${dialogueId}/${lineIndex}/${language}` -> blob URL
type AudioCache = Map<string, string>;

// ---------------------------------------------------------------------------
// Module-level state
// ---------------------------------------------------------------------------

let _cachedManifest: DialogueLinesManifest | null = null;
let _audioCache: AudioCache = new Map();
let _preloadPromise: Promise<void> | null = null;

// ---------------------------------------------------------------------------
// Manifest
// ---------------------------------------------------------------------------

/**
 * Fetch and cache the dialogue-lines-catalogue.json for the given episode folder.
 * The catalogue maps dialogueIds to audio folder names and line counts.
 */
export async function fetchManifest(sessionScriptFolder: string): Promise<DialogueLinesManifest> {
  const url = `${getDialogueLinesBaseUrl()}/${sessionScriptFolder}/dialogue-lines-catalogue.json`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`[DialogueLines] Failed to fetch catalogue: ${response.status} ${url}`);
  }
  const manifest: DialogueLinesManifest = await response.json();
  _cachedManifest = manifest;
  return manifest;
}

export function getCachedManifest(): DialogueLinesManifest | null {
  return _cachedManifest;
}

// ---------------------------------------------------------------------------
// Preload
// ---------------------------------------------------------------------------

/**
 * Download all audio files for the given manifest and language, caching them as Blob URLs.
 * Only downloads files for the requested language to minimize bandwidth.
 * If preload is already in progress, returns the same promise.
 */
export function preloadSessionScriptAudio(
  manifest: DialogueLinesManifest,
  language: string,
): Promise<void> {
  if (_preloadPromise) return _preloadPromise;

  _preloadPromise = _doPreload(manifest, language).finally(() => {
    _preloadPromise = null;
  });
  return _preloadPromise;
}

async function _doPreload(
  manifest: DialogueLinesManifest,
  language: string,
): Promise<void> {
  const baseUrl = getDialogueLinesBaseUrl();
  const entries = Object.entries(manifest.dialogues);

  // Build a flat list of all audio URLs to fetch
  const tasks: Array<{ cacheKey: string; url: string }> = [];
  for (const [dialogueId, entry] of entries) {
    for (let i = 1; i <= entry.lineCount; i++) {
      const lineId = `line${String(i).padStart(2, '0')}`;
      const cacheKey = `${dialogueId}/${lineId}/${language}`;
      if (!_audioCache.has(cacheKey)) {
        const url = `${baseUrl}/${manifest.sessionScriptFolder}/${language}/${entry.folder}/${lineId}.mp3`;
        tasks.push({ cacheKey, url });
      }
    }
  }

  // Download in parallel batches to avoid too many concurrent requests
  const BATCH_SIZE = 10;
  for (let i = 0; i < tasks.length; i += BATCH_SIZE) {
    const batch = tasks.slice(i, i + BATCH_SIZE);
    await Promise.allSettled(
      batch.map(async ({ cacheKey, url }) => {
        try {
          const res = await fetch(url);
          if (!res.ok) return; // Graceful degradation — missing audio is not fatal
          const blob = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          _audioCache.set(cacheKey, blobUrl);
        } catch {
          // Network errors are non-fatal — dialogue still works without audio
        }
      }),
    );
  }
}

// ---------------------------------------------------------------------------
// Lookup
// ---------------------------------------------------------------------------

/**
 * Returns the cached Blob URL for a dialogue line's audio.
 * Returns null if the audio is not available (not downloaded or failed).
 */
export function getAudioUrl(
  dialogueId: string,
  lineIndex: number,
  language: string,
): string | null {
  const lineId = `line${String(lineIndex + 1).padStart(2, '0')}`;
  const cacheKey = `${dialogueId}/${lineId}/${language}`;
  return _audioCache.get(cacheKey) ?? null;
}

// ---------------------------------------------------------------------------
// Reset
// ---------------------------------------------------------------------------

/**
 * Clear all cached audio Blob URLs and manifest.
 * Should be called when the player disconnects or the match ends.
 */
export function resetDialogueLines(): void {
  for (const blobUrl of _audioCache.values()) {
    URL.revokeObjectURL(blobUrl);
  }
  _audioCache = new Map();
  _cachedManifest = null;
  _preloadPromise = null;
}
